import { useEffect, useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import {
  formatDisplayDate,
  parseTypedDate,
  typedDateValidator,
  type RevisedAccountsDialogData,
  type RevisedAccountsView,
} from '@/core/models/report-options.models';
import { notifyError } from '@/core/notify/notify';
import { reportOptionsService } from '@/core/services/reportOptions';
import { validateForm } from './common';

type Name = 'supplementaryText' | 'supplementaryDetail' | 'supplementaryApprovalDate' | 'approvingContactId';

/** Angular SupplementaryDialogComponent: the note text, approval date and approving director of a supplementary note. */
export function SupplementaryDialog({
  data,
  dialogRef,
}: DialogComponentProps<RevisedAccountsDialogData, RevisedAccountsView>) {
  const view = data?.view;
  const directors = view?.directors ?? [];
  // A director who is no longer a client contact cannot stay selected.
  const initialContact = directors.some((d) => d.id === view?.approvingContactId)
    ? (view?.approvingContactId ?? null)
    : null;

  const [supplementaryText, setSupplementaryText] = useState(view?.supplementaryText ?? '');
  const [supplementaryDetail, setSupplementaryDetail] = useState(view?.supplementaryDetail ?? '');
  const [supplementaryApprovalDate, setApprovalDate] = useState(formatDisplayDate(view?.supplementaryApprovalDate));
  const [approvingContactId, setApprovingContactId] = useState<string | null>(initialContact);
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<Name>>(new Set());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (directors.length === 0) {
      notifyError('Add the directors as contacts of this client to choose who approves the note.');
    }
    return () => {
      alive.current = false;
    };
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A value of spaces only is empty (the API trims it); the API accepts up to 20000 characters.
  const required = (value: string) => value.trim().length > 0 && value.length <= 20000;
  const valid: Record<Name, boolean> = {
    supplementaryText: required(supplementaryText),
    supplementaryDetail: required(supplementaryDetail),
    supplementaryApprovalDate: typedDateValidator({ value: supplementaryApprovalDate }) === null,
    approvingContactId: approvingContactId !== null && approvingContactId !== undefined && approvingContactId !== '',
  };
  const touch = (name: Name) => setTouched((current) => new Set(current).add(name));
  const invalid = (name: Name) => touched.has(name) && !valid[name];

  const onSave = () => {
    if (!view || isSaving) return;
    // Both texts are trimmed before they are validated.
    const text = `${supplementaryText ?? ''}`.trim();
    const detail = `${supplementaryDetail ?? ''}`.trim();
    setSupplementaryText(text);
    setSupplementaryDetail(detail);
    const allValid = required(text) && required(detail) && valid.supplementaryApprovalDate && valid.approvingContactId;
    if (!validateForm(allValid)) {
      setTouched(
        new Set<Name>(['supplementaryText', 'supplementaryDetail', 'supplementaryApprovalDate', 'approvingContactId']),
      );
      return;
    }
    setIsSaving(true);
    reportOptionsService
      .saveSupplementary({
        clientId: view.clientId,
        accountingPeriodId: view.accountingPeriodId,
        supplementaryText: text,
        supplementaryDetail: detail,
        supplementaryApprovalDate: parseTypedDate(supplementaryApprovalDate),
        approvingContactId,
      })
      .then(
        (saved) => dialogRef.close(saved),
        (error) => alive.current && notifyError(error),
      )
      .finally(() => alive.current && setIsSaving(false));
  };

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Revision by Supplementary Note</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div>
            <p className="mb-1">
              A statement should be made detailing the aspects in which this note revises the original accounts.
            </p>
            <div className="form-field textarea">
              <p className="fw-bold">
                Enter text to be included within the revision by supplementary note <span className="text-red">*</span>
              </p>
              <TextareaField
                fieldClassName="wp-100 mb-1"
                required
                maxLength={20000}
                value={supplementaryText}
                invalid={invalid('supplementaryText')}
                onChange={(event) => setSupplementaryText(event.target.value)}
                onBlur={() => touch('supplementaryText')}
              />
              <TextareaField
                fieldClassName="wp-100"
                required
                maxLength={20000}
                value={supplementaryDetail}
                invalid={invalid('supplementaryDetail')}
                onChange={(event) => setSupplementaryDetail(event.target.value)}
                onBlur={() => touch('supplementaryDetail')}
              />
            </div>

            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">Supplementary Date of Approval</p>
                <InputField
                  fieldClassName="w-200"
                  placeholder="DD/MM/YYYY"
                  charMode="number"
                  extraChars="/.-"
                  maxLength={10}
                  value={supplementaryApprovalDate}
                  invalid={invalid('supplementaryApprovalDate')}
                  onChange={(event) => setApprovalDate(event.target.value)}
                  onBlur={() => touch('supplementaryApprovalDate')}
                />
              </div>
              <div className="form-field">
                <p className="fw-bold">
                  Director Approving <span className="text-red">*</span>
                </p>
                <Select
                  className={`w-200${invalid('approvingContactId') ? ' ng-invalid ng-touched' : ''}`}
                  items={directors}
                  bindLabel="name"
                  bindValue="id"
                  value={approvingContactId}
                  onChange={(value) => setApprovingContactId(value as string | null)}
                  onBlur={() => touch('approvingContactId')}
                />
              </div>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
